import { getFechaOperativaDate } from './fechaOperativaStore';

/** Redondea a 2 decimales evitando errores de floating point (p. ej. 5000.0002). */
export const roundMoney = (value: number): number => Math.round((value || 0) * 100) / 100;

/** Tolerancia para comparar montos en pesos (centavos). */
export const MONEY_TOLERANCE = 0.01;

export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

/** Formato de edición es-AR: miles con punto y 2 decimales. */
export const formatPesosInput = (value: number): string => {
    const safe = Number.isFinite(value) ? value : 0;
    return safe.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/** Parsea texto ingresado en formato pesos (es-AR o pegado). */
export const parsePesosInput = (raw: string): number => {
    const cleaned = (raw ?? '').replace(/\s/g, '').replace(/\$/g, '');
    if (!cleaned) return 0;

    const isNegative = cleaned.startsWith('-');
    const unsigned = isNegative ? cleaned.slice(1) : cleaned;
    if (!unsigned) return 0;

    const hasComma = unsigned.includes(',');
    const hasDot = unsigned.includes('.');
    let normalized = unsigned;

    if (hasComma && hasDot) {
        normalized = unsigned.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
        normalized = unsigned.replace(',', '.');
    }

    normalized = normalized.replace(/[^0-9.]/g, '');
    const parts = normalized.split('.');
    if (parts.length > 2) {
        normalized = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    const n = Number(normalized);
    if (!Number.isFinite(n)) return 0;
    return isNegative ? -Math.abs(n) : n;
};

/**
 * Formatea un precio con el símbolo de moneda según el tipoMoneda
 * @param amount - El monto a formatear
 * @param simbolo - El símbolo de la moneda (ej: "$", "USD", "€")
 * @returns String formateado con el símbolo delante del precio
 */
/**
 * Trunca un monto a N decimales sin errores de punto flotante (IEEE 754).
 * Usar en lugar de Math.trunc(n * 100) / 100 para importes monetarios.
 */
export function truncToDecimals(value: number | string, decimals = 2): number {
    const normalized = String(value).trim().replace(',', '.');
    if (!normalized || normalized === '-' || normalized === '.') return 0;
    const negative = normalized.startsWith('-');
    const unsigned = negative ? normalized.slice(1) : normalized;
    const [intPart = '0', fracPart = ''] = unsigned.split('.');
    const safeInt = intPart === '' ? '0' : intPart;
    const truncatedFrac = fracPart.slice(0, decimals).padEnd(decimals, '0');
    const n = Number(`${negative ? '-' : ''}${safeInt}.${truncatedFrac}`);
    return Number.isFinite(n) ? n : 0;
}

export const formatCurrencyWithSymbol = (amount: number, simbolo?: string | null): string => {
    if (!simbolo) {
        // Si no hay símbolo, usar formato ARS por defecto
        return formatCurrency(amount);
    }
    
    // Formatear el número con separador de miles y 2 decimales
    const formattedAmount = new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
    
    // Colocar el símbolo delante del precio
    return `${simbolo}${formattedAmount}`;
};

/**
 * Convierte un string de fecha (YYYY-MM-DD) a un objeto Date en la zona horaria local
 * Evita el problema de que new Date("YYYY-MM-DD") se interprete como UTC
 * @param dateString - String en formato YYYY-MM-DD
 * @returns Date en zona horaria local
 */
export const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    // month - 1 porque los meses en Date van de 0 a 11
    return new Date(year, month - 1, day);
};

/**
 * Fecha local como YYYY-MM-DD (p. ej. para defaults de input type="date").
 * Evita usar toISOString(), que usa UTC y puede cambiar el día calendario.
 * Sin argumento usa la fecha operativa de sesión (si está activa) o el reloj real.
 */
export const toLocalDateStringYMD = (d?: Date): string => {
    const date = d ?? getFechaOperativaDate();
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/**
 * YYYY-MM-DD para query params de filtros.
 * Si ya es string de fecha, se envía tal cual (evita el desfase de new Date("YYYY-MM-DD") en UTC).
 */
export const toQueryDateYMD = (value: Date | string): string =>
    typeof value === 'string' ? value : toLocalDateStringYMD(value);

/**
 * DD/MM/YYYY a partir de Date o string ISO/fecha del API, usando calendario local.
 * Para strings solo-fecha (YYYY-MM-DD) usa parseLocalDate (evita desfase UTC).
 */
export const formatDateLocal = (value: Date | string | null | undefined): string => {
    if (value == null || value === '') return '';
    let date: Date;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            date = parseLocalDate(trimmed);
        } else {
            date = new Date(value);
        }
    } else {
        date = value;
    }
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Formatea una fecha en formato DD/MM/YYYY
 * @param dateString - String de fecha (ISO o formato fecha)
 * @returns String formateado en DD/MM/YYYY
 */
export const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
        const trimmed = dateString.trim();
        const date = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
            ? parseLocalDate(trimmed)
            : new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (error) {
        return '';
    }
};

/**
 * Formatea una fecha con hora en formato DD/MM/YYYY HH:mm
 * @param dateString - String de fecha (ISO o formato fecha)
 * @returns String formateado en DD/MM/YYYY HH:mm
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        return '';
    }
};

const AR_TIME_ZONE = 'America/Argentina/Buenos_Aires';

/**
 * DateTime del API guardado en UTC (a menudo sin sufijo Z). Lo muestra en hora de Argentina.
 */
export function parseApiUtcDateTime(value: string | Date | null | undefined): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    const iso = hasTz || !trimmed.includes('T') ? trimmed : `${trimmed}Z`;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** DD/MM/YYYY HH:mm:ss en zona America/Argentina/Buenos_Aires. */
export function formatDateTimeAr(value: string | Date | null | undefined): string {
    const date = parseApiUtcDateTime(value);
    if (!date) return '';
    return new Intl.DateTimeFormat('es-AR', {
        timeZone: AR_TIME_ZONE,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).format(date);
}

/** Inicio del día calendario en Argentina, en ISO UTC. */
export function argentinaDayStartIso(ymd: string): string {
    return new Date(`${ymd}T00:00:00.000-03:00`).toISOString();
}

/** Fin del día calendario en Argentina, en ISO UTC. */
export function argentinaDayEndIso(ymd: string): string {
    return new Date(`${ymd}T23:59:59.999-03:00`).toISOString();
}

/**
 * Notas por renglón (p. ej. columnas CHAR(n) en SQL): el API puede devolver espacios de relleno al final.
 * Se recorta con trimEnd para la UI y para no enviar cadenas de 500 caracteres vacíos al guardar.
 */
export function normalizeItemNotas(value: string | undefined | null, maxLen = 500): string {
    return String(value ?? '')
        .trimEnd()
        .slice(0, maxLen);
}

/** Formatea CUIT argentino como xx-xxxxxxxx-x (máx. 11 dígitos). */
export function formatCuit(value?: string | number | null): string {
    const digits = normalizeCuitForStorage(value);
    if (digits.length <= 2) return digits;
    if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

/** CUIT solo dígitos para persistencia (máx. 11). */
export function normalizeCuitForStorage(value?: string | number | null): string {
    return String(value ?? '').replace(/\D/g, '').slice(0, 11);
}

function normalizeDocPart(value: string | number | null | undefined): string {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (!s || s === '0') return '';
    if (/^\d+(\.0+)?$/.test(s)) return s.replace(/\.0+$/, '');
    return s;
}

/** CUIT y DNI concatenados con "/" cuando ambos existen. */
/** Letra-sucursal-número como recibos / devoluciones tesorería (ej. X-00012-00000002). */
export function formatComprobanteTesoreria(
    letra?: string | null,
    nroSuc?: number | null,
    nroComprobante?: number | null
): string {
    if (!letra || nroSuc == null || nroComprobante == null) return '-';

    const formatPart = (value: number | string, length: number) => {
        if (value == null) return ''.padStart(length, '0');
        const numeric = Number(value);
        if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
        return String(value).padStart(length, '0');
    };

    const letraNormalizada = letra.trim().toUpperCase();
    if (!letraNormalizada) return '-';

    return `${letraNormalizada}-${formatPart(nroSuc, 5)}-${formatPart(nroComprobante, 8)}`;
}

/** Número comercial del documento (letra-sucursal-nro). Vacío si todavía no hay numeración. */
export function formatNroDocumentoVenta(
    letra?: string | null,
    nroSuc?: number | null,
    nroComprobante?: number | null,
): string {
    const nro = Number(nroComprobante);
    if (!String(letra ?? '').trim() || nroSuc == null || !Number.isFinite(nro) || nro <= 0) {
        return '';
    }
    const formatted = formatComprobanteTesoreria(letra, nroSuc, nro);
    return formatted === '-' ? '' : formatted;
}

/** Tipo de formulario + comprobante padded (ej. RECIBO X-00012-00000043). */
export function formatComprobanteCajaConTipo(
    nombreTipoFormulario?: string | null,
    letra?: string | null,
    nroSuc?: number | null,
    nroComprobante?: number | null,
    nroFormulario?: number | null
): string {
    const partes: string[] = [];
    const tipo = (nombreTipoFormulario ?? '').trim();
    if (tipo) partes.push(tipo);

    const comp = formatComprobanteTesoreria(letra, nroSuc, nroComprobante);
    if (comp !== '-') {
        partes.push(comp);
    } else if (nroFormulario != null && nroFormulario > 0) {
        partes.push(`#${nroFormulario}`);
    }

    return partes.length > 0 ? partes.join(' ') : '-';
}

/** Cliente para listados y cabeceras: "123 - Nombre". */
export function formatClienteConNro(
    nroCliente?: number | null,
    nombre?: string | null,
): string {
    const nro = Number(nroCliente) || 0;
    const nom = String(nombre ?? '').trim();
    if (nro > 0 && nom) return `${nro} - ${nom}`;
    if (nro > 0) return String(nro);
    return nom || '-';
}

/** Proveedor para listados y cabeceras: "123 - Nombre". */
export const formatProveedorConNro = formatClienteConNro;

export function formatCuitDniDisplay(
    cuit?: string | null,
    dni?: string | number | null,
    emptyFallback = '-'
): string {
    const c = normalizeDocPart(cuit);
    const d = normalizeDocPart(dni);
    if (c && d) return `${c}/${d}`;
    if (c) return c;
    if (d) return d;
    return emptyFallback;
}